import { WorkflowInstanceDto } from '../dto/case.dto';
import { CreateCaseDto } from '../dto/create-case.dto';
import { UpdateCaseDto } from '../dto/update-case-dto';
import { ContactAttemptDto } from 'src/crs/dto/contact-attempt.dto';
import { PaginateQuery, paginateQueryToURLParams } from 'src/api/pagination/dtos';
import { convertFilterToPaginateQueryParams } from 'src/utils/gridView';
import { apiClient } from 'src/careflow/ph-core/api_client/api_client';
export interface CaseResponse {
  createdBy: string;
  createdOn: string;
  updatedBy: string;
  updatedOn: string;
  caseId: string;
  caseUUID: string;
  fhirId: string;
  workflowInstanceId: number;
  workflowStatus: string;
  workflowSubstatus: string;
  workflowStageId: string;
  workflowStageName: string;
  workflowStageOrder: number;
  workflowDispositionReason: string;
  workflowDispositionNote: string;
  workflowDispositionOn: string;
  workflowDispositionAt: string;
  workflowOwnedBy: string;
  workflowScope: string;
  workflowReferenceName: string;
  indexedFhirRefUri: string;
  indexedSubjectFhirRefUri: string;
  indexedsubjectMrn: string;
  indexedsubjectFullName: string;
  indexedsubjectPhone: string;
  indexedsubjectEmail: string;
  indexedsubjectDob: Date;
  indexedStatus: string;
  internalNumber: string;
  subjectDOBFormatted: string;
  subjectAge: string;
  createdOnFormatted: string;
}

export interface PaginatedCaseResponse {
  data: CaseResponse[];
  links: {
    current: string;
  };
  meta: {
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
  };
}

export interface PaginatedCaseDto {
  data: WorkflowInstanceDto[];
  links: {
    current: string;
  };
  meta: {
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
  };
  stats?: any;
}

export interface CaseContactAttemptResponse {
  caseId: string;
  fhirId: string;
  caseContactAttempId: string;
  caseContactAttempUuid: string;
  caseContactAttempOutcome: string;
  caseContactAttempContactOn: string;
  caseContactAttempNextContactOn: string;
  caseContactAttempNoteId: string;
  noteId: string;
  noteUuid: string;
  noteType: string;
  noteText: string;
}

const apiBase = 'crs/case';

const caseApi = {
  getAll: async (queryParams: PaginateQuery): Promise<PaginatedCaseResponse | undefined> => {
    const urlParams = paginateQueryToURLParams(queryParams);

    let subjectFhirId = '';
    if (queryParams.filter?.indexedSubjectFhirRefUri) {
      subjectFhirId = `&filter.${
        Object.entries(queryParams.filter || []).map(([key, val]) => `${key}=${val}`)[0]
      }`;
    }

    let carePlanFhirId = '';
    if (queryParams.filter?.fhirId) {
      carePlanFhirId = `&filter.${
        Object.entries(queryParams.filter || []).map(([key, val]) => `${key}=$in:${val}`)[0]
      }`;
    }

    let filterIndexedStatus = '';
    if (queryParams.filter?.indexedStatus) {
      filterIndexedStatus = `&filter.${
        Object.entries(queryParams.filter || []).map(([key, val]) => `${key}=${val}`)[1]
      }`;
    }
    const filters = convertFilterToPaginateQueryParams(queryParams?.filter ?? {});
    const url = `${apiBase}/?${urlParams.toString()}${subjectFhirId}${carePlanFhirId}${filterIndexedStatus}${filters}`;

    return apiClient.get<PaginatedCaseResponse | undefined>(url);
  },

  getAllContactAttempt: async (fhirId: string): Promise<CaseContactAttemptResponse[] | undefined> =>
    apiClient.get<CaseContactAttemptResponse[] | undefined>(`${apiBase}/contact-attempt/${fhirId}`),

  getOne: async (fhirId: string): Promise<CaseResponse | undefined> =>
    apiClient.get<CaseResponse | undefined>(`${apiBase}/${fhirId}`),

  create: async (payload: CreateCaseDto): Promise<CaseResponse | undefined> =>
    apiClient.post<CreateCaseDto, CaseResponse | undefined>(`${apiBase}/`, payload),

  update: async (fhirId: string, payload: UpdateCaseDto): Promise<CaseResponse | undefined> =>
    apiClient.put<UpdateCaseDto, CaseResponse | undefined>(`${apiBase}/${fhirId}`, payload),

  createContactAttempt: async (payload: ContactAttemptDto): Promise<any | undefined> =>
    apiClient.post<ContactAttemptDto, any | undefined>(`${apiBase}/contact-attempt`, payload),

  getFamilyMembers: async (fhirId: string): Promise<any[] | undefined> =>
    apiClient.get<any[] | undefined>(`${apiBase}/${fhirId}/family-members`),

  getStatistics: async (id: string): Promise<any | undefined> =>
    apiClient.get<any | undefined>(`${apiBase}/statistics/${id}`),
};

export const CaseService = {
  getAll: async (queryParams: PaginateQuery): Promise<PaginatedCaseDto | undefined> => {
    const cases = await caseApi.getAll(queryParams);
    if (!cases) return undefined;
    const dtos = cases.data.map((r) => {
      const dto = new WorkflowInstanceDto();
      dto.fromWorkflowInstanceResponse(r);
      return dto;
    });
    const mapped: PaginatedCaseDto = { ...cases, data: dtos };
    return mapped;
  },

  getOne: async (fhirId: string): Promise<CaseResponse | undefined> => {
    const caseResponse = await caseApi.getOne(fhirId);
    if (!caseResponse) return undefined;
    return caseResponse;
  },

  getContactAttempt: async (fhirId: string): Promise<CaseContactAttemptResponse[] | undefined> => {
    const contactAttempts = await caseApi.getAllContactAttempt(fhirId);
    if (!contactAttempts) return undefined;
    return contactAttempts;
  },

  create: async (payload: CreateCaseDto): Promise<WorkflowInstanceDto | undefined> => {
    const cases = await caseApi.create(payload);
    if (!cases) return undefined;
    const dto = new WorkflowInstanceDto();
    dto.fromWorkflowInstanceResponse(cases);
    return dto;
  },

  update: async (
    fhirId: string,
    payload: UpdateCaseDto
  ): Promise<WorkflowInstanceDto | undefined> => {
    const cases = await caseApi.update(fhirId, payload);
    if (!cases) return undefined;
    const dto = new WorkflowInstanceDto();
    dto.fromWorkflowInstanceResponse(cases);
    return dto;
  },

  createContactAttempt: async (payload: ContactAttemptDto): Promise<any | undefined> => {
    const contactAttempt = await caseApi.createContactAttempt(payload);
    if (!contactAttempt) return undefined;
    return contactAttempt;
  },

  getFamilyMembers: async (fhirId: string): Promise<any[]> => {
    try {
      const familyMembers = await caseApi.getFamilyMembers(fhirId);
      return familyMembers || [];
    } catch (error) {
      return [];
    }
  },

  getStatistics: async (id: string): Promise<any | undefined> => {
    const statistics = await caseApi.getStatistics(id);
    if (!statistics) return undefined;
    return statistics;
  },
};
